import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Moon, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const SERIF = '"Cormorant Garamond", Georgia, serif';
const LENGTH = 30;
const CYCLE = 10; // 4s in, 6s out

// 30 seconds of soft music and breathing (no words), then the installation starts.
export const NightStartOverlay = ({ onFinish, onCancel }) => {
  const music = useRef(null);
  const [t, setT] = useState(0);
  useEffect(() => {
    const a = new Audio(`${API}/api/audio/ambient/nocturne.mp3`);
    a.volume = 0; a.loop = true; music.current = a;
    a.play().catch(() => {});
    const start = Date.now();
    const iv = setInterval(() => {
      const s = (Date.now() - start) / 1000;
      setT(s);
      a.volume = Math.max(0, Math.min(0.5, s < 4 ? s / 8 : s > LENGTH - 4 ? (LENGTH - s) / 8 : 0.5));
      if (s >= LENGTH) { clearInterval(iv); a.pause(); onFinish(); }
    }, 100);
    return () => { clearInterval(iv); a.pause(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const phase = t % CYCLE;
  const inhale = phase < 4;
  const scale = inhale ? 0.6 + 0.4 * (phase / 4) : 1 - 0.4 * ((phase - 4) / 6);
  return (
    <div className="fixed inset-0 z-[90] flex flex-col items-center justify-center" style={{ background: "#070D0B" }} data-testid="night-start-overlay">
      <button type="button" onClick={onCancel} aria-label="Close" data-testid="night-start-cancel" className="absolute top-6 right-6" style={{ color: "rgba(241,236,221,0.6)" }}><X size={22} /></button>
      <div className="w-56 h-56 rounded-full" style={{ transform: `scale(${scale})`, transition: "transform 120ms linear", background: "radial-gradient(circle, rgba(255,244,214,0.55) 0%, rgba(215,187,122,0.22) 50%, transparent 72%)" }} />
      <p className="mt-10 text-3xl italic" style={{ fontFamily: SERIF, color: "#F1ECDD" }} data-testid="night-start-phase">{inhale ? "Breathe in" : "Breathe out"}</p>
      <p className="mt-3 text-xs uppercase tracking-[0.3em]" style={{ color: "rgba(215,187,122,0.8)" }} data-testid="night-start-remaining">{Math.max(0, Math.ceil(LENGTH - t))}s · then your installation</p>
    </div>
  );
};

export const NightStartButton = ({ onClick }) => (
  <button type="button" onClick={onClick} data-testid="night-start-button" className="w-full rounded-full py-3.5 text-sm inline-flex items-center justify-center gap-2 transition-transform duration-200 hover:-translate-y-0.5"
    style={{ border: "1px solid #D7BB7A", color: "#D7BB7A", background: "rgba(215,187,122,0.06)" }}>
    <Moon size={15} /> Guided night start · 30 seconds
  </button>
);

// Account setting: play the 30-second night start before every listen after 8pm.
const PREF = "night_start_default";
const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } });
export const nightStartDue = () => localStorage.getItem(PREF) === "1" && new Date().getHours() >= 20;

export const NightStartToggle = () => {
  const [on, setOn] = useState(() => localStorage.getItem(PREF) === "1");
  useEffect(() => {
    axios.get(`${API}/api/guide-streaks/prefs`, authH()).then((r) => { setOn(r.data.night_start); localStorage.setItem(PREF, r.data.night_start ? "1" : "0"); }).catch(() => {});
  }, []);
  const flip = () => {
    const v = !on; setOn(v); localStorage.setItem(PREF, v ? "1" : "0");
    axios.put(`${API}/api/guide-streaks/prefs`, { night_start: v }, authH())
      .catch(() => { setOn(!v); localStorage.setItem(PREF, v ? "0" : "1"); });
  };
  return (
    <button type="button" onClick={flip} role="switch" aria-checked={on} data-testid="night-start-toggle"
      className="w-full mb-6 flex items-center justify-between rounded-2xl px-5 py-3 text-left"
      style={{ background: "rgba(245,224,173,0.03)", border: "1px solid rgba(215,187,122,0.25)" }}>
      <span className="flex items-center gap-3">
        <Moon size={16} style={{ color: "#D7BB7A" }} />
        <span>
          <span className="block text-sm" style={{ color: "#F1ECDD" }}>Night start before bedtime listens</span>
          <span className="block text-xs" style={{ color: "rgba(241,236,221,0.55)" }}>30 seconds of soft music and breathing, every listen after 8pm</span>
          <ListeningNights className="mt-1 text-xs" style={{ color: "#D7BB7A" }} />
        </span>
      </span>
      <span className="w-10 h-6 rounded-full relative transition-colors duration-200" style={{ background: on ? "#D7BB7A" : "rgba(241,236,221,0.18)" }}>
        <span className="absolute top-1 w-4 h-4 rounded-full bg-white transition-transform duration-200" style={{ transform: `translateX(${on ? 20 : 4}px)` }} />
      </span>
    </button>
  );
};

const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

// "N nights in a row": any installation listen from 6pm counts for that night.
export const ListeningNights = ({ className = "", style }) => {
  const [n, setN] = useState(0);
  const [tonight, setTonight] = useState(false);
  useEffect(() => {
    if (!localStorage.getItem("reclaim_token")) return;
    axios.get(`${API}/api/guide-streaks/nights`, { ...authH(), params: { today: today() } })
      .then((r) => { setN(r.data.nights || 0); setTonight(!!r.data.tonight); }).catch(() => {});
  }, []);
  if (!n) return null;
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`} style={style} data-testid="listening-nights-streak">
      <Moon size={12} /> {n === 1 ? (tonight ? "Listened tonight" : "Listened last night") : `${n} nights in a row listening`}
    </span>
  );
};
